/**
 * Week View — 7 days × staff swim-lanes (premium grid)
 *
 * Each day has a column; within each day, all staff swim-lanes share the time axis.
 * Drag&Drop between days/times (staff implicitly preserved when dropping inside the
 * same staff lane; switching staff requires Team Columns view).
 *
 * Props:
 *   appointments: [{appointment_id, staff_id, service_name, client_name,
 *                   start_time | time, duration | total_duration, date, status}]
 *   staff:        [{staff_id, name, color, role, service_ids, photo_url}]
 *   services:     [{service_id, name, is_active}]
 *   weekStart:    Date (Monday)
 *   onMoveAppointment(appt, newDate, newTime) → Promise
 *   onClickAppointment(appt)
 *   onClickSlot(day, time, staffId?) → opens new-appt dialog
 *   workingHours: { start, end, slotMinutes }
 *   language: 'it'|'en'
 *   filterServiceId: string|null
 *   onFilterServiceChange(serviceId|null)
 */
import React, { useMemo, useState, useCallback } from "react";
import { format, addDays, isSameDay } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";
import { Filter, X, AlertCircle, Briefcase, AlertTriangle } from "lucide-react";

const STATUS_TONE = {
  scheduled: "from-blue-500 to-cyan-500",
  confirmed: "from-emerald-500 to-teal-500",
  pending:   "from-amber-500 to-orange-500",
  awaiting_confirmation: "from-orange-500 to-amber-500",
  completed: "from-violet-500 to-fuchsia-500",
  cancelled: "from-rose-500 to-red-500",
  no_show:   "from-slate-500 to-slate-700",
};

const HOUR_HEIGHT_PX  = 56;
const TIME_GUTTER_W   = 56;
const DAY_COL_MIN_W   = 180;

function buildHourLabels(start = "08:00", end = "20:00") {
  const [sh] = start.split(":").map(Number);
  const [eh] = end.split(":").map(Number);
  const out = [];
  for (let h = sh; h <= eh; h++) {
    out.push(`${String(h).padStart(2, "0")}:00`);
  }
  return out;
}

function timeToY(time, firstHour) {
  if (!time) return 0;
  const [h, m] = String(time).split(":").map(Number);
  if (Number.isNaN(h)) return 0;
  return (h - firstHour) * HOUR_HEIGHT_PX + (m / 60) * HOUR_HEIGHT_PX;
}

function yToTime(y, firstHour) {
  const totalMinutes = Math.round((y / HOUR_HEIGHT_PX) * 60 / 15) * 15;
  const h = firstHour + Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function apptToMinutes(a) {
  const t = a.start_time || a.time;
  if (!t) return null;
  const [h, m] = String(t).split(":").map(Number);
  const start  = h * 60 + (m || 0);
  const dur    = parseInt(a.total_duration || a.duration || 30, 10);
  return { start, dur, end: start + dur };
}

function annotateOverlaps(items) {
  const out = items.map(it => ({ ...it, _conflict: false }));
  for (let i = 0; i < out.length; i++) {
    for (let j = i + 1; j < out.length; j++) {
      const a = apptToMinutes(out[i]);
      const b = apptToMinutes(out[j]);
      if (!a || !b) continue;
      // Per-staff conflict only: same staff_id required to flag overlap
      if (out[i].staff_id !== out[j].staff_id) continue;
      if (a.end > b.start && b.end > a.start) {
        out[i]._conflict = true;
        out[j]._conflict = true;
      }
    }
  }
  return out;
}

const WeekView = ({
  appointments = [],
  staff = [],
  services = [],
  weekStart = new Date(),
  onMoveAppointment,
  onClickAppointment,
  onClickSlot,
  workingHours = { start: "08:00", end: "20:00", slotMinutes: 30 },
  language = "it",
  filterServiceId = null,
  onFilterServiceChange,
}) => {
  const hours = useMemo(() => buildHourLabels(workingHours.start, workingHours.end),
    [workingHours.start, workingHours.end]);
  const firstHour = parseInt(workingHours.start.split(":")[0], 10);
  const containerHeight = hours.length * HOUR_HEIGHT_PX + 8;

  const weekDays = useMemo(() =>
    [...Array(7)].map((_, i) => addDays(weekStart, i)),
    [weekStart]);

  const [dragId,    setDragId]    = useState(null);
  const [hoverDrop, setHoverDrop] = useState(null);  // {date, time}

  const tenantHasServices = services && services.length > 0;
  const filterService = useMemo(
    () => services.find(s => s.service_id === filterServiceId),
    [services, filterServiceId]
  );

  // Apply service filter to appointments (drop those whose service_id doesn't match
  // when filter set — but show all if appointment lacks service_id).
  const filteredAppts = useMemo(() => {
    if (!filterServiceId) return appointments || [];
    return (appointments || []).filter(a => {
      const ids = a.service_ids || (a.service_id ? [a.service_id] : []);
      if (ids.length === 0) return true;
      return ids.includes(filterServiceId);
    });
  }, [appointments, filterServiceId]);

  // Group by day
  const byDay = useMemo(() => {
    const m = {};
    weekDays.forEach(d => { m[format(d, "yyyy-MM-dd")] = []; });
    filteredAppts.forEach(a => {
      const key = (a.date || "").slice(0, 10);
      if (m[key]) m[key].push(a);
    });
    Object.keys(m).forEach(k => {
      m[k].sort((a, b) => {
        const ta = (a.start_time || a.time || "00:00");
        const tb = (b.start_time || b.time || "00:00");
        return ta.localeCompare(tb);
      });
      m[k] = annotateOverlaps(m[k]);
    });
    return m;
  }, [filteredAppts, weekDays]);

  const staffById = useMemo(() => {
    const m = {};
    staff.forEach(s => { m[s.staff_id] = s; });
    return m;
  }, [staff]);

  const handleDragStart = (e, appt) => {
    if (appt.is_blocked || appt.status === "blocked") { e.preventDefault(); return; }
    setDragId(appt.appointment_id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", appt.appointment_id); } catch { /* noop */ }
    try {
      const img = document.createElement("div");
      img.style.cssText = "position:absolute;top:-9999px;width:1px;height:1px;";
      document.body.appendChild(img);
      e.dataTransfer.setDragImage(img, 0, 0);
      setTimeout(() => document.body.removeChild(img), 0);
    } catch { /* unsupported */ }
  };

  const handleDragEnd = () => { setDragId(null); setHoverDrop(null); };

  const handleColumnDragOver = (e, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const time = yToTime(Math.max(0, y), firstHour);
    setHoverDrop({ date, time });
  };

  const handleDrop = (e, date) => {
    e.preventDefault();
    if (!dragId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const time = yToTime(Math.max(0, y), firstHour);
    const appt = (appointments || []).find(a => a.appointment_id === dragId);
    if (appt && onMoveAppointment) {
      onMoveAppointment(appt, date, time);
    }
    setDragId(null);
    setHoverDrop(null);
  };

  const handleSlotClick = useCallback((e, day) => {
    if (!onClickSlot) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const time = yToTime(Math.max(0, y), firstHour);
    onClickSlot(day, time);
  }, [onClickSlot, firstHour]);

  // === Render ===
  const now = new Date();
  const nowH = now.getHours();
  const nowM = now.getMinutes();
  const nowY = (nowH - firstHour) * HOUR_HEIGHT_PX + (nowM / 60) * HOUR_HEIGHT_PX;

  return (
    <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden shadow-sm" data-testid="week-view">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 dark:border-white/5 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900/60 dark:to-slate-900/30">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Week View</p>
          <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
            {format(weekDays[0], "d MMM", { locale: language === "it" ? itLocale : enUS })} – {format(weekDays[6], "d MMM yyyy", { locale: language === "it" ? itLocale : enUS })}
          </p>
        </div>

        {tenantHasServices && onFilterServiceChange && (
          <div className="flex items-center gap-1 rounded-full bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 px-2 py-1" data-testid="week-service-filter">
            <Filter className="w-3 h-3 text-slate-400" />
            <select
              className="text-[11px] font-bold bg-transparent outline-none text-slate-700 dark:text-slate-200 pr-1 max-w-[180px]"
              value={filterServiceId || ""}
              onChange={(e) => onFilterServiceChange(e.target.value || null)}
              data-testid="week-service-filter-select"
            >
              <option value="">{language === "it" ? "Tutti i servizi" : "All services"}</option>
              {services.map(s => (
                <option key={s.service_id} value={s.service_id}>{s.name}</option>
              ))}
            </select>
            {filterService && (
              <button type="button" onClick={() => onFilterServiceChange(null)}
                className="text-slate-400 hover:text-slate-600" title={language === "it" ? "Cancella filtro" : "Clear"}
                data-testid="week-service-filter-clear">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </header>

      <div className="overflow-x-auto">
        <div className="flex" style={{ minWidth: TIME_GUTTER_W + weekDays.length * DAY_COL_MIN_W }}>
          {/* Time gutter */}
          <div className="shrink-0 sticky left-0 bg-white dark:bg-slate-900/40 z-10 border-r border-slate-100 dark:border-white/5"
               style={{ width: TIME_GUTTER_W }}>
            <div className="h-14 border-b border-slate-100 dark:border-white/5" />
            <div className="relative" style={{ height: containerHeight }}>
              {hours.map((h, i) => (
                <div key={h}
                     className="absolute left-0 right-0 border-t border-slate-100 dark:border-white/5 text-[10px] font-mono font-bold text-slate-400 pl-2 pt-0.5"
                     style={{ top: i * HOUR_HEIGHT_PX }}>
                  {h}
                </div>
              ))}
            </div>
          </div>

          {/* Day columns */}
          {weekDays.map(day => {
            const dateStr = format(day, "yyyy-MM-dd");
            const ap = byDay[dateStr] || [];
            const isToday = isSameDay(day, new Date());
            const showNow = isToday && nowY > 0 && nowY < containerHeight;
            return (
              <div key={dateStr} className="shrink-0 border-r border-slate-100 dark:border-white/5"
                   style={{ width: DAY_COL_MIN_W, minWidth: DAY_COL_MIN_W }}
                   data-testid={`week-col-${dateStr}`}>
                {/* Column header */}
                <div className={`h-14 px-3 flex flex-col items-center justify-center border-b border-slate-100 dark:border-white/5 sticky top-0 z-[5] ${isToday ? "bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/30 dark:to-slate-900/40" : "bg-white dark:bg-slate-900/40"}`}>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                    {format(day, "EEE", { locale: language === "it" ? itLocale : enUS })}
                  </p>
                  <p className={`text-lg font-black leading-none ${isToday ? "text-blue-600 dark:text-blue-300" : "text-slate-800 dark:text-slate-200"}`}>
                    {format(day, "d")}
                  </p>
                </div>

                {/* Column body */}
                <div
                  className={`relative ${dragId ? "bg-blue-50/40 dark:bg-blue-900/10" : ""}`}
                  style={{ height: containerHeight }}
                  onDragOver={(e) => handleColumnDragOver(e, dateStr)}
                  onDrop={(e) => handleDrop(e, dateStr)}
                  onClick={(e) => handleSlotClick(e, day)}
                  data-testid={`week-col-body-${dateStr}`}
                >
                  {/* Hour grid lines */}
                  {hours.map((h, i) => (
                    <div key={h}
                         className="absolute left-0 right-0 border-t border-slate-100 dark:border-white/5"
                         style={{ top: i * HOUR_HEIGHT_PX }} />
                  ))}
                  {/* Half-hour ticks */}
                  {hours.map((h, i) => (
                    <div key={`half-${h}`}
                         className="absolute left-0 right-0 border-t border-dashed border-slate-100/70 dark:border-white/[0.04]"
                         style={{ top: i * HOUR_HEIGHT_PX + HOUR_HEIGHT_PX / 2 }} />
                  ))}

                  {/* Now line */}
                  {showNow && (
                    <div className="absolute left-0 right-0 z-[3] pointer-events-none" style={{ top: nowY }}>
                      <div className="h-px bg-gradient-to-r from-rose-500 to-pink-500" />
                      <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-rose-500 shadow" />
                    </div>
                  )}

                  {/* Drop ghost */}
                  {hoverDrop?.date === dateStr && dragId && (
                    <div className="absolute left-1.5 right-1.5 rounded-xl border-2 border-dashed border-blue-400 bg-blue-50/70 dark:bg-blue-900/30 pointer-events-none transition-all"
                         style={{ top: timeToY(hoverDrop.time, firstHour), height: 32 }}
                         data-testid={`week-drop-ghost-${dateStr}`}>
                      <div className="text-[10px] font-mono font-bold text-blue-700 dark:text-blue-300 px-2 pt-1">
                        {hoverDrop.time}
                      </div>
                    </div>
                  )}

                  {/* Appointments */}
                  {ap.map(appt => {
                    const startTime = appt.start_time || appt.time;
                    const startY    = timeToY(startTime, firstHour);
                    const duration  = Math.max(15, parseInt(appt.total_duration || appt.duration || 30, 10));
                    const cardH     = Math.max(20, (duration / 60) * HOUR_HEIGHT_PX - 2);
                    const tone      = STATUS_TONE[appt.status] || STATUS_TONE.scheduled;
                    const isBlock   = appt.is_blocked || appt.kind === "block" || appt.status === "blocked";
                    const conflict  = appt._conflict;
                    const member    = staffById[appt.staff_id];
                    const accent    = member?.color || "#6366f1";
                    return (
                      <div
                        key={appt.appointment_id}
                        draggable={!isBlock}
                        onDragStart={(e) => handleDragStart(e, appt)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => { e.stopPropagation(); if (onClickAppointment) onClickAppointment(appt); }}
                        className={`absolute left-1.5 right-1.5 rounded-xl px-2 py-1.5 cursor-pointer shadow-sm transition-all hover:shadow-md hover:-translate-y-px overflow-hidden text-white ${
                          isBlock
                            ? "bg-gradient-to-r from-slate-700 to-slate-900 opacity-90"
                            : `bg-gradient-to-r ${tone}`
                        } ${dragId === appt.appointment_id ? "opacity-50 scale-[0.98]" : ""} ${conflict ? "ring-2 ring-rose-400 ring-offset-1" : ""}`}
                        style={{ top: startY + 1, height: cardH, cursor: isBlock ? "default" : "grab", borderLeft: `3px solid ${accent}` }}
                        data-testid={`week-appt-${appt.appointment_id}`}
                        title={`${appt.client_name || ""} · ${appt.service_name || ""}${member ? ` · ${member.name}` : ""}${conflict ? " · ⚠ sovrapposizione" : ""}`}
                      >
                        <div className="flex items-center justify-between text-[10px] font-mono font-bold opacity-95">
                          <span>{startTime}</span>
                          {conflict && (
                            <AlertTriangle className="w-3 h-3 text-amber-100"
                              aria-label={language === "it" ? "Sovrapposizione" : "Conflict"} />
                          )}
                        </div>
                        <p className="text-[11px] font-bold leading-tight truncate mt-0.5">
                          {appt.client_name || (isBlock ? (appt.reason || (language === "it" ? "Bloccato" : "Blocked")) : "—")}
                        </p>
                        {cardH > 30 && member?.name && (
                          <p className="text-[9px] opacity-80 truncate flex items-center gap-0.5 mt-0.5">
                            <Briefcase className="w-2 h-2" /> {member.name}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WeekView;
